/**
 * Utilities for lightweight Plotly previews (dashboard cards, thumbnails).
 * Keeps traces small and layouts minimal for staticPlot rendering.
 */

const DEFAULT_MAX_POINTS = 150;

/**
 * Same order as ChartCard.vue grouped traces so thumbnails match full agentic plots.
 */
export const CHART_GROUP_COLORS = [
  "#FF6B6B",
  "#4ECDC4",
  "#45B7D1",
  "#FFA07A",
  "#98D8C8",
  "#F7DC6F",
  "#BB8FCE",
  "#85C1E2",
];

/** Fallback / single-trace accents */
const COLORS = CHART_GROUP_COLORS;

/** Grouped histograms — same as ChartCard / Studio (blue / coral stacks). */
const STUDIO_HISTOGRAM_COLORS = [
  "rgba(0, 100, 250, 0.6)",
  "rgba(255, 99, 71, 0.6)",
  "rgba(50, 205, 50, 0.6)",
  "rgba(255, 215, 0, 0.6)",
  "rgba(147, 112, 219, 0.6)",
  "rgba(255, 165, 0, 0.6)",
];

export function downsampleRows(rows, maxPoints = DEFAULT_MAX_POINTS) {
  if (!rows || !rows.length) return [];
  if (rows.length <= maxPoints) return rows;
  const step = Math.ceil(rows.length / maxPoints);
  const out = [];
  for (let i = 0; i < rows.length; i += step) out.push(rows[i]);
  return out;
}

function normalizeChartType(chartType) {
  return String(chartType ?? "")
    .trim()
    .toLowerCase();
}

/** Downsample ordered {x,y} pairs without breaking alignment. */
function downsamplePairs(pairs, maxPoints) {
  if (!pairs || !pairs.length) return [];
  if (pairs.length <= maxPoints) return pairs;
  const step = Math.ceil(pairs.length / maxPoints);
  const out = [];
  for (let i = 0; i < pairs.length; i += step) out.push(pairs[i]);
  return out;
}

/** First-seen legend/group order in table rows (matches typical full-chart legend assignment). */
function uniqueGroupKeysInRowOrder(rows, groupCol) {
  const keys = [];
  const seen = new Set();
  if (!groupCol || !rows) return keys;
  for (let i = 0; i < rows.length; i++) {
    const g = rows[i][groupCol];
    if (g === undefined || g === null || g === "") continue;
    if (seen.has(g)) continue;
    seen.add(g);
    keys.push(g);
  }
  return keys;
}

/** Keep (x,y) from the same row — do not filter x and y independently. */
function pairScatterPoints(rows, xCol, yCol) {
  const pairs = [];
  if (!rows || !xCol || !yCol) return pairs;
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const rawX = row[xCol];
    const rawY = row[yCol];
    if (rawX == null || rawX === "" || rawY == null || rawY === "") continue;
    const y = toNum(rawY);
    if (y == null) continue;
    pairs.push({ x: rawX, y });
  }
  return pairs;
}

function finalizeScatterPairs(pairs, traceMode) {
  if (!pairs.length) return pairs;
  const allNumericX = pairs.every((p) => {
    const vx = p.x;
    return (
      (typeof vx === "number" && !Number.isNaN(vx)) ||
      (typeof vx === "string" && !Number.isNaN(parseFloat(vx)) && Number.isFinite(parseFloat(vx)))
    );
  });
  if (traceMode === "lines" && allNumericX) {
    return [...pairs]
      .map((p) => ({
        x: typeof p.x === "number" ? p.x : parseFloat(p.x),
        y: p.y,
      }))
      .sort((a, b) => a.x - b.x);
  }
  return pairs;
}

export function buildFilterQueryParams(chartFilter) {
  const queryParams = {};
  if (!chartFilter) return queryParams;

  const arrayKeys = [
    "asset",
    "field",
    "well",
    "reservoir",
    "fluidType",
    "sampleType",
    "fluidClassification",
    "fluidSampleID",
    "fluidSampleContainerID",
  ];
  arrayKeys.forEach((key) => {
    if (chartFilter[key] && Array.isArray(chartFilter[key]) && chartFilter[key].length) {
      queryParams[key] = JSON.stringify(chartFilter[key]);
    }
  });
  if (chartFilter.source && chartFilter.source !== "") {
    queryParams.source = `"${chartFilter.source}"`;
  }
  ["depth", "formationPressure", "formationTemperature", "sampleDate"].forEach((key) => {
    if (chartFilter[key]) {
      queryParams[key] = JSON.stringify(chartFilter[key]);
    }
  });
  return queryParams;
}

export function getPreviewPlotConfigsFromDashboard(dashboard, maxPlots = 4) {
  if (!dashboard) return [];
  if (Array.isArray(dashboard.Charts) && dashboard.Charts.length) {
    return dashboard.Charts.slice(0, maxPlots).map((c) => ({
      data: typeof c === "object" ? c : { ID: c },
      chartData: null,
    }));
  }
  if (Array.isArray(dashboard.ChartsID) && dashboard.ChartsID.length) {
    return dashboard.ChartsID.slice(0, maxPlots).map((id) => ({
      data: { ID: id },
      chartData: null,
    }));
  }
  return [];
}

export function getDashboardPlotCount(dashboard) {
  if (!dashboard) return 0;
  if (Array.isArray(dashboard.Charts) && dashboard.Charts.length) {
    return dashboard.Charts.length;
  }
  if (Array.isArray(dashboard.ChartsID) && dashboard.ChartsID.length) {
    return dashboard.ChartsID.length;
  }
  return 0;
}

function getTraceType(chartType) {
  const t = normalizeChartType(chartType);
  const typeMap = {
    "scatter plot": "scatter",
    "line plot": "scatter",
    "bar chart": "bar",
    bar: "bar",
    barplot: "bar",
    histogram: "histogram",
    "2d histogram": "histogram2d",
    "3d scatter": "scatter3d",
    bubble: "scatter",
  };
  return typeMap[t] || "scatter";
}

function getTraceMode(chartType) {
  const t = normalizeChartType(chartType);
  if (t === "line plot") return "lines";
  if (t === "scatter plot" || t === "bubble") return "markers";
  if (t === "bar chart" || t === "bar" || t === "barplot" || t === "histogram" || t === "2d histogram") {
    return null;
  }
  return "markers";
}

function calculateBinSize(data, bins) {
  const n = Number(bins) || 20;
  const numericData = (data || []).filter((value) => typeof value === "number" && !Number.isNaN(value));
  if (numericData.length === 0) return 1;
  const minValue = Math.min(...numericData);
  const maxValue = Math.max(...numericData);
  const span = maxValue - minValue;
  if (span === 0) return 1;
  return span / n;
}

function toNum(v) {
  if (v == null || v === "") return null;
  const numVal = typeof v === "number" ? v : parseFloat(v);
  return Number.isNaN(numVal) ? null : numVal;
}

function stripTraceForMini(trace) {
  if (!trace || typeof trace !== "object") return trace;
  const t = { ...trace };
  delete t.hovertemplate;
  delete t.customdata;
  delete t.text;
  if (t.marker && typeof t.marker === "object") {
    t.marker = { ...t.marker };
    delete t.marker.line;
  }
  if (t.line && typeof t.line === "object") {
    t.line = { ...t.line, width: Math.min(t.line.width || 2, 2) };
  }
  if (t.type === "histogram2d") {
    t.showscale = false;
    delete t.colorbar;
  }
  return t;
}

function buildScatterLikeTraces(chart, rows, maxPoints = DEFAULT_MAX_POINTS) {
  const traceType = getTraceType(chart.ChartType);
  const traceMode = getTraceMode(chart.ChartType);
  const yColumns = chart.YAxisColumn
    ? chart.YAxisColumn.split(",").map((col) => col.trim()).filter(Boolean)
    : [];

  if (!chart.XAxisColumn || !yColumns.length) return [];

  const gCol = chart.GroupColumn && String(chart.GroupColumn).trim();
  if (gCol && chart.YAxisColumn) {
    const yCols = [...yColumns];
    if (yCols.length > 1 && yCols[yCols.length - 1] === chart.GroupColumn) {
      yCols.pop();
    }
    const valueYCol = yCols[0];
    if (!valueYCol) return [];

    const keysInOrder = uniqueGroupKeysInRowOrder(rows, gCol);
    if (!keysInOrder.length) return [];

    const nGroups = keysInOrder.length;
    const perTraceBudget = Math.max(
      28,
      Math.min(200, Math.floor((maxPoints * Math.min(12, nGroups)) / Math.max(1, nGroups)))
    );

    const traces = [];
    keysInOrder.forEach((group, idx) => {
      const groupRows = rows.filter((row) => row[gCol] === group);
      let pairs = pairScatterPoints(groupRows, chart.XAxisColumn, valueYCol);
      pairs = finalizeScatterPairs(pairs, traceMode);
      pairs = downsamplePairs(pairs, perTraceBudget);
      if (!pairs.length) return;

      const color = CHART_GROUP_COLORS[idx % CHART_GROUP_COLORS.length];
      const trace = {
        x: pairs.map((p) => p.x),
        y: pairs.map((p) => p.y),
        type: traceType,
        mode: traceMode,
        name: String(group),
      };
      if (traceMode === "markers" || traceMode === "lines+markers") {
        trace.marker = { size: 4, color };
      }
      if (traceMode === "lines" || traceMode === "lines+markers") {
        trace.line = { width: 1.5, color };
      }
      traces.push(trace);
    });
    return traces;
  }

  let pairs = pairScatterPoints(rows, chart.XAxisColumn, yColumns[0]);
  pairs = finalizeScatterPairs(pairs, traceMode);
  pairs = downsamplePairs(pairs, maxPoints);
  if (!pairs.length) return [];

  const trace = {
    x: pairs.map((p) => p.x),
    y: pairs.map((p) => p.y),
    type: traceType,
    mode: traceMode,
  };
  const color = CHART_GROUP_COLORS[0];
  if (traceMode === "markers" || traceMode === "lines+markers") {
    trace.marker = { size: 4, color };
  }
  if (traceMode === "lines" || traceMode === "lines+markers") {
    trace.line = { width: 1.5, color };
  }
  return [trace];
}

function histParseX(value) {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  if (typeof value === "string") {
    const num = parseFloat(value);
    if (!Number.isNaN(num) && Number.isFinite(num)) return num;
    return value;
  }
  return value;
}

/** 1D histogram(s), including Field-style grouping — matches ChartCard. */
function buildHistogramTraces(chart, rows) {
  const xCol = chart.XAxisColumn;
  if (!xCol || !rows?.length) return [];

  const numBins = Math.max(1, Math.floor(Number(chart.NumXBins)) || 20);
  const borderColor = "rgba(0, 0, 0, 1)";
  const borderWidth = 1;
  const colors = STUDIO_HISTOGRAM_COLORS;

  const allXRaw = [];
  for (let i = 0; i < rows.length; i++) {
    const v = histParseX(rows[i][xCol]);
    if (v !== null && v !== undefined && v !== "") allXRaw.push(v);
  }
  if (!allXRaw.length) return [];

  const isNumericData = allXRaw.every((value) => {
    if (typeof value === "number") return !Number.isNaN(value);
    if (typeof value === "string") {
      const num = parseFloat(value);
      return !Number.isNaN(num) && Number.isFinite(num);
    }
    return false;
  });

  const makeNumericXbins = (numericSorted) => {
    if (!numericSorted.length) return undefined;
    const minVal = numericSorted[0];
    const maxVal = numericSorted[numericSorted.length - 1];
    const range = maxVal - minVal;
    if (range <= 0) return undefined;
    const adjustedMinVal = Math.max(0, minVal);
    const adjustedRange = maxVal - adjustedMinVal;
    const padding = adjustedRange * 0.001;
    const binSize = (adjustedRange + 2 * padding) / numBins;
    return {
      start: Math.max(0, adjustedMinVal - padding),
      end: maxVal + padding,
      size: binSize,
    };
  };

  const gCol = chart.GroupColumn && String(chart.GroupColumn).trim();

  if (gCol) {
    const keys = [
      ...new Set(rows.map((r) => r[gCol]).filter((v) => v !== undefined && v !== null && v !== "")),
    ];
    keys.sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));

    let unifiedXbinsConfig;
    if (isNumericData) {
      const nums = allXRaw.map((v) => (typeof v === "string" ? parseFloat(v) : v));
      nums.sort((a, b) => a - b);
      unifiedXbinsConfig = makeNumericXbins(nums);
    }

    const traces = [];
    keys.forEach((key, index) => {
      const groupRows = rows.filter((row) => row[gCol] === key);
      const validXData = groupRows
        .map((r) => histParseX(r[xCol]))
        .filter((v) => v !== null && v !== undefined && v !== "");
      if (!validXData.length) return;

      let sortedData;
      if (isNumericData) {
        sortedData = validXData.map((v) => (typeof v === "string" ? parseFloat(v) : v)).sort((a, b) => a - b);
      } else {
        sortedData = validXData;
      }

      const legendName =
        String(key).length > 24 ? `${String(key).slice(0, 23)}…` : String(key);
      const useUnified = !!(isNumericData && unifiedXbinsConfig);
      const trace = {
        x: sortedData,
        type: "histogram",
        autobinx: !useUnified,
        nbinsx: numBins,
        name: legendName,
        marker: {
          color: colors[index % colors.length],
          line: { color: borderColor, width: borderWidth },
        },
      };
      if (useUnified) {
        trace.xbins = unifiedXbinsConfig;
      }
      traces.push(trace);
    });
    return traces;
  }

  let sortedData;
  if (isNumericData) {
    sortedData = allXRaw.map((v) => (typeof v === "string" ? parseFloat(v) : v)).sort((a, b) => a - b);
  } else {
    sortedData = allXRaw;
  }

  let xbinsConfig;
  if (isNumericData && sortedData.length) {
    xbinsConfig = makeNumericXbins(sortedData);
  }

  const useBins = !!(isNumericData && xbinsConfig);
  return [
    {
      x: sortedData,
      type: "histogram",
      autobinx: !useBins,
      nbinsx: numBins,
      xbins: useBins ? xbinsConfig : undefined,
      marker: {
        color: colors[0],
        line: { color: borderColor, width: borderWidth },
      },
    },
  ];
}

function buildHistogram2dTrace(chart, rows) {
  if (!chart.XAxisColumn || !chart.YAxisColumn) return [];
  const yCol = chart.YAxisColumn.split(",")[0].trim();
  const xData = rows.map((item) => item[chart.XAxisColumn]);
  const yData = rows.map((item) => item[yCol]);
  const trace = {
    x: xData,
    y: yData,
    type: "histogram2d",
    colorscale: "Blues",
    showscale: false,
    autobinx: false,
    autobiny: false,
    xbins: { size: calculateBinSize(xData, chart.NumXBins) },
    ybins: { size: calculateBinSize(yData, chart.NumYBins) },
  };
  return [trace];
}

function barCountCategoryKey(val) {
  if (val == null || val === "") return null;
  if (typeof val === "object") return JSON.stringify(val);
  return val;
}

function compareBarCategoryDisplay(a, b) {
  const na = typeof a === "number" && !Number.isNaN(a);
  const nb = typeof b === "number" && !Number.isNaN(b);
  if (na && nb) return a - b;
  if (na && !nb) return -1;
  if (!na && nb) return 1;
  return String(a).localeCompare(String(b), undefined, { numeric: true });
}

/** Bar chart with no Y: counts per X category; optional GroupColumn → side-by-side colors like ChartCard. */
function buildBarCountTraces(chart, rows) {
  const xCol = chart.XAxisColumn;
  if (!xCol || !rows?.length) return [];

  const collectSortedCategories = (subset) => {
    const counts = new Map();
    for (let i = 0; i < subset.length; i++) {
      const val = subset[i][xCol];
      if (val == null || val === "") continue;
      const k = barCountCategoryKey(val);
      if (k === null) continue;
      if (!counts.has(k)) counts.set(k, { display: val, n: 0 });
      counts.get(k).n += 1;
    }
    return [...counts.values()].sort((u, v) => compareBarCategoryDisplay(u.display, v.display));
  };

  const gCol = chart.GroupColumn && String(chart.GroupColumn).trim();
  if (gCol) {
    const keysInOrder = uniqueGroupKeysInRowOrder(rows, gCol);
    const allEntries = collectSortedCategories(rows);
    if (!allEntries.length) return [];

    const sortedKeys = allEntries.map((e) => barCountCategoryKey(e.display));
    const keyToDisplay = new Map(allEntries.map((e) => [barCountCategoryKey(e.display), e.display]));

    const traces = [];
    keysInOrder.forEach((group, idx) => {
      const groupRows = rows.filter((row) => row[gCol] === group);
      const local = collectSortedCategories(groupRows);
      const localMap = new Map(local.map((e) => [barCountCategoryKey(e.display), e.n]));
      const xs = sortedKeys.map((k) => keyToDisplay.get(k));
      const ys = sortedKeys.map((k) => localMap.get(k) || 0);
      const name = String(group).length > 22 ? `${String(group).slice(0, 21)}…` : String(group);
      traces.push({
        x: xs,
        y: ys,
        type: "bar",
        name,
        marker: { color: CHART_GROUP_COLORS[idx % CHART_GROUP_COLORS.length] },
      });
    });
    return traces;
  }

  const entries = collectSortedCategories(rows);
  if (!entries.length) return [];
  return [
    {
      x: entries.map((e) => e.display),
      y: entries.map((e) => e.n),
      type: "bar",
      marker: { color: CHART_GROUP_COLORS[1] },
    },
  ];
}

function buildBarTrace(chart, rows) {
  if (!chart.XAxisColumn) return [];
  const yColRaw = chart.YAxisColumn ? String(chart.YAxisColumn).split(",")[0].trim() : "";
  if (!yColRaw) {
    return buildBarCountTraces(chart, rows);
  }

  const gCol = chart.GroupColumn && String(chart.GroupColumn).trim();
  const yParts = chart.YAxisColumn.split(",").map((c) => c.trim()).filter(Boolean);
  const ySeries = [...yParts];
  if (gCol && ySeries.length > 1 && ySeries[ySeries.length - 1] === gCol) {
    ySeries.pop();
  }
  const yCol = ySeries[0] || yColRaw;

  if (gCol && yCol) {
    const keys = uniqueGroupKeysInRowOrder(rows, gCol);
    const traces = [];
    keys.forEach((group, idx) => {
      const groupRows = rows.filter((r) => r[gCol] === group);
      let pairs = pairScatterPoints(groupRows, chart.XAxisColumn, yCol);
      const numericX =
        pairs.length > 0 &&
        pairs.every((p) => {
          const vx = p.x;
          return (
            (typeof vx === "number" && !Number.isNaN(vx)) ||
            (typeof vx === "string" && !Number.isNaN(parseFloat(vx)) && Number.isFinite(parseFloat(vx)))
          );
        });
      pairs = finalizeScatterPairs(pairs, numericX ? "lines" : null);
      if (!pairs.length) return;
      const name = String(group).length > 22 ? `${String(group).slice(0, 21)}…` : String(group);
      traces.push({
        x: pairs.map((p) => p.x),
        y: pairs.map((p) => p.y),
        type: "bar",
        name,
        marker: { color: CHART_GROUP_COLORS[idx % CHART_GROUP_COLORS.length] },
      });
    });
    if (traces.length) return traces;
  }

  let pairs = pairScatterPoints(rows, chart.XAxisColumn, yCol);
  const numericXAll =
    pairs.length > 0 &&
    pairs.every((p) => {
      const vx = p.x;
      return (
        (typeof vx === "number" && !Number.isNaN(vx)) ||
        (typeof vx === "string" && !Number.isNaN(parseFloat(vx)) && Number.isFinite(parseFloat(vx)))
      );
    });
  pairs = finalizeScatterPairs(pairs, numericXAll ? "lines" : null);
  if (!pairs.length) return [];
  return [
    {
      x: pairs.map((p) => p.x),
      y: pairs.map((p) => p.y),
      type: "bar",
      marker: { color: CHART_GROUP_COLORS[1] },
    },
  ];
}

function build3dTrace(chart, rows) {
  if (!chart.XAxisColumn || !chart.YAxisColumn || !chart.ZAxisColumn) return [];
  const yCol = chart.YAxisColumn.split(",")[0].trim();
  const xData = rows.map((r) => r[chart.XAxisColumn]).filter((v) => v != null && v !== "");
  const yData = rows.map((r) => toNum(r[yCol])).filter((v) => v != null);
  const zData = rows.map((r) => toNum(r[chart.ZAxisColumn])).filter((v) => v != null);
  const n = Math.min(xData.length, yData.length, zData.length);
  if (!n) return [];
  return [
    {
      x: xData.slice(0, n),
      y: yData.slice(0, n),
      z: zData.slice(0, n),
      type: "scatter3d",
      mode: "markers",
      marker: { size: 2, color: COLORS[0] },
    },
  ];
}

/**
 * Turn saved chart metadata + row samples into Plotly traces for a mini preview.
 */
export function buildMiniTraces(chartMeta, rows, maxPoints = DEFAULT_MAX_POINTS) {
  if (!chartMeta || !rows || !rows.length) return [];

  const ctNorm = normalizeChartType(chartMeta.ChartType);
  const sampled = downsampleRows(rows, maxPoints);

  const yTrim = chartMeta.YAxisColumn ? String(chartMeta.YAxisColumn).trim() : "";
  const gTrim = chartMeta.GroupColumn ? String(chartMeta.GroupColumn).trim() : "";
  const lineOrScatter =
    ctNorm === "line plot" || ctNorm === "scatter plot" || ctNorm === "bubble";
  const useFullRowsForGroups = lineOrScatter && gTrim && yTrim;

  const barOrHist =
    ctNorm === "histogram" ||
    ctNorm === "2d histogram" ||
    ctNorm === "bar chart" ||
    ctNorm === "bar" ||
    ctNorm === "barplot";
  const useFullRowsForBarHistGroup = gTrim && barOrHist;

  const scatterInputRows = useFullRowsForGroups ? rows : sampled;

  let traces = [];
  if (ctNorm === "histogram") {
    traces = buildHistogramTraces(chartMeta, useFullRowsForBarHistGroup ? rows : sampled);
  } else if (ctNorm === "2d histogram") {
    traces = buildHistogram2dTrace(chartMeta, sampled);
  } else if (ctNorm === "bar chart" || ctNorm === "bar" || ctNorm === "barplot") {
    const rowsForBar = useFullRowsForBarHistGroup ? rows : yTrim ? sampled : rows;
    traces = buildBarTrace(chartMeta, rowsForBar);
  } else if (ctNorm === "3d scatter") {
    traces = build3dTrace(chartMeta, sampled);
  } else {
    traces = buildScatterLikeTraces(chartMeta, scatterInputRows, maxPoints);
  }

  return (traces || []).map(stripTraceForMini).filter(Boolean);
}

function miniAxisTemplate() {
  return {
    visible: false,
    showgrid: false,
    showticklabels: false,
    zeroline: false,
    fixedrange: true,
    title: { text: "" },
  };
}

export function buildMiniLayout(chartMeta, layoutOptions = {}) {
  const isDark = !!layoutOptions.isDark;
  const plotBg2d = isDark ? "rgba(31, 41, 55, 0.92)" : "rgba(245,247,250,0.35)";
  const sceneBg = isDark ? "rgb(31, 41, 55)" : "rgba(0,0,0,0)";
  const traces = layoutOptions.traces;
  const multiBarHist =
    Array.isArray(traces) &&
    traces.length > 1 &&
    traces.every((t) => t && (t.type === "bar" || t.type === "histogram"));

  const is3d = chartMeta && chartMeta.ChartType === "3D Scatter";
  if (is3d) {
    return {
      transition: { duration: 0 },
      paper_bgcolor: "rgba(0,0,0,0)",
      plot_bgcolor: "rgba(0,0,0,0)",
      margin: { l: 0, r: 0, t: 0, b: 0, pad: 0 },
      showlegend: false,
      autosize: true,
      hovermode: false,
      scene: {
        bgcolor: sceneBg,
        xaxis: { ...miniAxisTemplate(), showbackground: !!isDark },
        yaxis: { ...miniAxisTemplate(), showbackground: !!isDark },
        zaxis: { ...miniAxisTemplate(), showbackground: !!isDark },
        camera: { eye: { x: 1.35, y: 1.35, z: 1.15 } },
      },
    };
  }
  const layout2d = {
    transition: { duration: 0 },
    paper_bgcolor: "rgba(0,0,0,0)",
    plot_bgcolor: plotBg2d,
    margin: { l: 0, r: 0, t: 0, b: 0, pad: 0 },
    xaxis: miniAxisTemplate(),
    yaxis: miniAxisTemplate(),
    showlegend: false,
    autosize: true,
    hovermode: false,
  };
  if (multiBarHist) {
    layout2d.barmode = "group";
    layout2d.bargap = 0.08;
  }
  return layout2d;
}

export function getMiniPlotlyConfig() {
  return {
    staticPlot: true,
    responsive: true,
    displayModeBar: false,
    displaylogo: false,
    scrollZoom: false,
    doubleClick: false,
    showTips: false,
  };
}

/**
 * Full plot config (same shape as ChartCard: { data, chartData }) -> minimal Plotly payload.
 * @param {Object} plotConfig — { data: chartMetadata, chartData: rows[]|null }
 * @param {Object} options — { maxPoints?: number, isDark?: boolean }
 * @returns {{ traces: any[], layout: Object, config: Object }|{ error: string }}
 */
export function transformToMiniPlot(plotConfig, options = {}) {
  const maxPoints = options.maxPoints ?? DEFAULT_MAX_POINTS;
  const isDark = !!options.isDark;
  const chartMeta = plotConfig?.data || plotConfig;
  const rows = plotConfig?.chartData;

  if (!chartMeta) {
    return { error: "Missing chart metadata" };
  }
  if (!rows || !Array.isArray(rows) || rows.length === 0) {
    return { error: "No chart rows" };
  }

  const traces = buildMiniTraces(chartMeta, rows, maxPoints);
  if (!traces.length) {
    return { error: "Could not build preview traces" };
  }

  const valid = traces.filter((trace) => {
    if (!trace || typeof trace !== "object" || !trace.type) return false;
    if (trace.type === "histogram") {
      return Array.isArray(trace.x);
    }
    if (trace.type === "histogram2d") {
      return Array.isArray(trace.x) && Array.isArray(trace.y);
    }
    if (trace.type === "scatter3d") {
      return Array.isArray(trace.x) && Array.isArray(trace.y) && Array.isArray(trace.z);
    }
    if (trace.type === "bar") {
      return Array.isArray(trace.x) && Array.isArray(trace.y);
    }
    return Array.isArray(trace.x) && Array.isArray(trace.y);
  });

  if (!valid.length) {
    return { error: "No valid traces" };
  }

  return {
    traces: valid,
    layout: buildMiniLayout(chartMeta, { isDark, traces: valid }),
    config: getMiniPlotlyConfig(),
  };
}
